import { Injectable } from '@angular/core';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { AlertModalV2Component } from '../components/modals/alert-modal-v2/alert-modal-v2.component';
import { AlertModalComponent } from '../components/modals/alert-modal/alert-modal.component';

export interface CustomAlertButton {
  /** Text to display on the button. */
  text: string;

  /** Button handler on click. */
  handler: () => void | Promise<any>;

  /**
   * Wether the action in `handler()` is asynchronous or not.
   * It will wait the process to finish if it is asynchronous.
   */
  isAsync?: boolean;

  /** Button CSS Class, following bootstrap's button class */
  class?: 'info' | 'warning' | 'danger' | 'light' | 'default';

  /** Use button custom class, it will override `class` properties if added. */
  customClass?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AlertModalService {
  constructor(private modal: NgbModal) {}

  public showInfoAlert(
    message: string,
    header?: string,
    buttonText?: string,
    buttonHandler?: () => void,
    options?: { [key: string]: any }
  ) {
    const modalRef = this.modal.open(AlertModalComponent, {
      size: 'sm',
      centered: true,
      ...options,
    });
    modalRef.componentInstance.instance = modalRef;
    modalRef.componentInstance.message = message;
    modalRef.componentInstance.header = header;
    modalRef.componentInstance.type = 'info';
    modalRef.componentInstance.primaryButtonText = buttonText || 'Ok';

    modalRef.result
      .then(() => {
        if (buttonHandler) buttonHandler();
      })
      .catch(() => {
        if (buttonHandler) buttonHandler();
      });

    return modalRef;
  }

  public showQuestionAlert(
    message: string,
    header?: string,
    primaryBtnText?: string,
    secondaryBtnText?: string,
    primaryBtnHandler?: () => void,
    secondaryBtnHandler?: () => void,
    options?: { [key: string]: any },
    btnClass?: string,
    preventDismissTriggerSecondaryBtn: boolean = false,
    disablePrimaryBtn: boolean = false
  ) {
    const modalRef = this.modal.open(AlertModalComponent, {
      size: 'sm',
      centered: true,
      backdrop: true,
      ...options,
    });
    modalRef.componentInstance.instance = modalRef;
    modalRef.componentInstance.message = message;
    modalRef.componentInstance.header = header;
    modalRef.componentInstance.type = 'question';
    modalRef.componentInstance.primaryButtonText = primaryBtnText || 'Ok';
    modalRef.componentInstance.secondaryButtonText = secondaryBtnText || 'Cancel';
    modalRef.componentInstance.btnClass = btnClass;
    modalRef.componentInstance.disablePrimaryBtn = disablePrimaryBtn;

    modalRef.result
      .then((result) => {
        if (result === true) {
          if (primaryBtnHandler) primaryBtnHandler();
        } else {
          if (secondaryBtnHandler) secondaryBtnHandler();
        }
      })
      .catch(() => {
        if (secondaryBtnHandler && !preventDismissTriggerSecondaryBtn) secondaryBtnHandler();
      });

    return modalRef;
  }

  public showCustomAlert(
    modalOptions: Partial<NgbModalOptions>,
    header: string,
    message: string,
    buttons: CustomAlertButton[]
  ) {
    const modalRef = this.modal.open(AlertModalV2Component, {
      size: 'sm',
      centered: true,
      backdrop: 'static',
      keyboard: false,
      ...modalOptions,
    });
    modalRef.componentInstance.modalRef = modalRef;
    modalRef.componentInstance.message = message;
    modalRef.componentInstance.header = header;
    modalRef.componentInstance.buttons = buttons;
  }
}
